import {
  createEhrPatientDemographicCallback,
  createEhrPatientProxiesCallback
} from '../utils';
import { getDefaultConfig } from './getDefaultConfig';

/**
 * Function for getting DBW configuration in the case of using the mychart login button in DBW.
 * Nothing happens in DBW until the user clicks on the login button at the top of the patient info page
 */

/**
 * Function that returns the config for Direct Book that allows for third-party EHR login.
 * @param {Object} props - The config properties
 * @param {function} props.log - The log/track function
 * @param {string} props.customerCode - represents a customer
 * @param {Object} props.location - the react-router location object
 * @param {Object} props.loginConfig - the EHR login config
 * @param {string} props.loginConfig.loginUrl - the login URL for the EHR system
 * @param {Function} props.loginConfig.getDeepLinkingUrl - function that creates a deep-linking URL
 * @param {Object} props.ehrData - supplemental EHR data to be added to the config
 * @param {Object} props.searchSDK
 * @returns the config supporting third-party EHR login
 */
export const getMychartLoginConfig = ({
  log,
  demographicLogMessages = {},
  featureFlags,
  type,
  customerCode,
  location,
  loginConfig,
  ehrData,
  tokens,
  config,
  searchSDK
}) => {
  const baseConfig = getDefaultConfig({
    customerCode,
    location,
    tokens,
    config,
    searchSDK
  });
  if (!ehrData) return baseConfig;

  const { loginUrl, getDeepLinkingUrl, showProxyBookingControls } = loginConfig;
  const { data, errors, clearData } = ehrData;
  if (!data) return baseConfig;

  const { pid = null, pid_type = null, source_system = null } = data;
  // Once we have the pid and source_system, we can assume the login was successful
  const isLoggedIn = pid != null && source_system != null;
  const ehrProps = isLoggedIn
    ? {
        ehr: {
          pid,
          pid_type,
          source_system,
          ...featureFlags
        }
      }
    : {};

  const deepLinkingUrl = location.pathname || '';
  // Gets us /book/1835433 vs /book/1835433/patient-info (which incorrectly becomes /book/1835433/patient-info/patient-info when deeplinking)
  const baseDeepLinkingUrl = deepLinkingUrl.substring(
    0,
    deepLinkingUrl.lastIndexOf('/')
  );

  return {
    ...baseConfig,
    ...ehrProps,
    thirdPartyLoginProps: {
      type,
      isLoggedIn,
      login: {
        callback: (deepLinkingParams) => {
          const deepLinkingUrl = getDeepLinkingUrl(
            baseDeepLinkingUrl,
            deepLinkingParams
          );
          window.history.replaceState(null, '', deepLinkingUrl);
          window.location.href = loginUrl;
        },
        url: loginUrl
      },
      logout: {
        callback: (deepLinkingParams) => {
          const deepLinkingUrl = getDeepLinkingUrl(
            baseDeepLinkingUrl,
            deepLinkingParams
          );
          window.location.href = deepLinkingUrl;
          // need to handle anything related to logout
          if (clearData) {
            clearData();
          }
        }
      },
      errors,
      showProxyBookingControls
    },
    services: {
      ...baseConfig.services,
      getEhrPatientDemographics: createEhrPatientDemographicCallback(
        log,
        customerCode,
        demographicLogMessages
      ),
      getEhrPatientProxyDemographics: createEhrPatientProxiesCallback(
        log,
        customerCode
      )
    },
    showApptSummary: true
  };
};
